import { useEffect, useState } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useExperiment, useFeature, useFeatures } from '@xing-com/hub';

type UsePayOrConsentStateResult = {
  shouldHoldConsentUi: boolean;
  shouldRenderPayOrConsentPlaceholder: boolean;
};

const CONSENT_CHANGE_EVENTS = new Set(['ACCEPT_ALL', 'DENY_ALL', 'SAVE']);
const USERCENTRICS_INITIALIZATION_TIMEOUT_MS = 5000;
const USERCENTRICS_CONSENT_EVENT = 'UC_CONSENT';
const USERCENTRICS_INITIALIZED_EVENT = 'UC_UI_INITIALIZED';
const USERCENTRICS_UI_CMP_EVENT = 'UC_UI_CMP_EVENT';

async function areAllConsentsAccepted(): Promise<boolean | undefined> {
  if (typeof window === 'undefined' || !window.__ucCmp?.getConsentDetails) {
    return undefined;
  }

  try {
    const consentDetails = await window.__ucCmp.getConsentDetails();
    return consentDetails.consent.status === 'ALL_ACCEPTED';
  } catch {
    return undefined;
  }
}

function subscribeToUsercentricsConsentState(
  onConsentStateChange: (acceptedAllConsents: boolean) => void
): () => void {
  let isSubscribed = true;
  let usercentricsInitializationTimeoutId: number | undefined;

  const clearUsercentricsInitializationTimeout = (): void => {
    if (usercentricsInitializationTimeoutId === undefined) {
      return;
    }

    window.clearTimeout(usercentricsInitializationTimeoutId);
    usercentricsInitializationTimeoutId = undefined;
  };

  const updateAcceptedAllConsents = (fallbackValue?: boolean): void => {
    void areAllConsentsAccepted().then((allConsentsAccepted) => {
      if (!isSubscribed) {
        return;
      }

      const acceptedAllConsents = allConsentsAccepted ?? fallbackValue;

      if (acceptedAllConsents === undefined) {
        return;
      }

      onConsentStateChange(acceptedAllConsents);
      clearUsercentricsInitializationTimeout();
    });
  };

  updateAcceptedAllConsents();

  usercentricsInitializationTimeoutId = window.setTimeout(() => {
    updateAcceptedAllConsents(false);
  }, USERCENTRICS_INITIALIZATION_TIMEOUT_MS);

  const handleUsercentricsInitialized = (): void => {
    updateAcceptedAllConsents();
  };

  const handleUsercentricsConsentEvent = (): void => {
    updateAcceptedAllConsents();
  };

  const handleUsercentricsUiEvent = (event: Event): void => {
    const cmpEvent = event as CustomEvent<{ type?: string }>;

    if (
      !cmpEvent.detail?.type ||
      !CONSENT_CHANGE_EVENTS.has(cmpEvent.detail.type)
    ) {
      return;
    }

    updateAcceptedAllConsents();
  };

  window.addEventListener(
    USERCENTRICS_INITIALIZED_EVENT,
    handleUsercentricsInitialized
  );
  window.addEventListener(USERCENTRICS_UI_CMP_EVENT, handleUsercentricsUiEvent);
  window.addEventListener(
    USERCENTRICS_CONSENT_EVENT,
    handleUsercentricsConsentEvent
  );

  return () => {
    isSubscribed = false;
    clearUsercentricsInitializationTimeout();
    window.removeEventListener(
      USERCENTRICS_INITIALIZED_EVENT,
      handleUsercentricsInitialized
    );
    window.removeEventListener(
      USERCENTRICS_UI_CMP_EVENT,
      handleUsercentricsUiEvent
    );
    window.removeEventListener(
      USERCENTRICS_CONSENT_EVENT,
      handleUsercentricsConsentEvent
    );
  };
}

function useUsercentricsAcceptedAllConsents(enabled: boolean): {
  acceptedAllConsents: boolean | undefined;
  isLoadingConsentData: boolean;
} {
  const [acceptedAllConsents, setAcceptedAllConsents] = useState<
    boolean | undefined
  >();

  useEffect(() => {
    if (!enabled) {
      setAcceptedAllConsents(undefined);
      return;
    }

    return subscribeToUsercentricsConsentState(setAcceptedAllConsents);
  }, [enabled]);

  return {
    acceptedAllConsents,
    isLoadingConsentData: enabled && acceptedAllConsents === undefined,
  };
}

export function usePayOrConsentState(): UsePayOrConsentStateResult {
  const { isLoggedIn } = useLoginState();
  const payOrConsentExperimentEnabled = useExperiment('ABACUS-546') === 'B';

  const { loading: loadingFeatures } = useFeatures();
  const isBasic = useFeature('isBasic', true);
  const activePayingMember =
    isLoggedIn && !loadingFeatures && isBasic === false;

  const { acceptedAllConsents, isLoadingConsentData } =
    useUsercentricsAcceptedAllConsents(
      payOrConsentExperimentEnabled && !activePayingMember
    );

  const isLoadingLoggedInEligibilityData =
    isLoggedIn && payOrConsentExperimentEnabled && loadingFeatures;
  const shouldUsePayOrConsentFlow =
    payOrConsentExperimentEnabled &&
    !activePayingMember &&
    !acceptedAllConsents &&
    !isLoadingConsentData;

  return {
    // Eligibility can depend on async viewer data or client-only Usercentrics
    // state, so we render no consent UI until the wall decision is known.
    shouldHoldConsentUi:
      isLoadingLoggedInEligibilityData || isLoadingConsentData,
    shouldRenderPayOrConsentPlaceholder: shouldUsePayOrConsentFlow,
  };
}
